import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  STATISTICS_PERIOD_PRESETS,
  StatisticsPeriod,
  StatisticsPeriodPreset,
} from './statisticsPeriod';

interface CallAnalysisPeriodFilterProps {
  value: StatisticsPeriod;
  onChange: (value: StatisticsPeriod) => void;
}

const parseDate = (date: string | null) => (date ? dayjs(date) : null);

const CallAnalysisPeriodFilter: React.FC<CallAnalysisPeriodFilterProps> = ({ value, onChange }) => {
  const { t } = useTranslation();

  const handlePreset = (
    _: React.MouseEvent<HTMLElement>,
    preset: StatisticsPeriodPreset | null,
  ) => {
    if (preset) onChange({ ...value, preset });
  };

  const handleDate = (key: 'start' | 'end') => (date: Dayjs | null) => {
    onChange({ ...value, [key]: date?.isValid() ? date.toISOString() : null });
  };

  const renderDatePicker = (key: 'start' | 'end') => (
    <DesktopDatePicker
      label={t(`campaignPage.analysisSummary.period.${key}`)}
      value={parseDate(value[key])}
      format="DD/MM/YYYY"
      onChange={handleDate(key)}
      minDate={key === 'end' ? (parseDate(value.start) ?? undefined) : undefined}
      maxDate={key === 'start' ? (parseDate(value.end) ?? undefined) : undefined}
      slotProps={{ textField: { size: 'small' } }}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
      <ToggleButtonGroup
        data-testid="statistics-period-select"
        exclusive
        size="small"
        value={value.preset}
        onChange={handlePreset}>
        {STATISTICS_PERIOD_PRESETS.map((preset) => (
          <ToggleButton key={preset} value={preset} data-testid={`statistics-period-${preset}`}>
            {t(`campaignPage.analysisSummary.period.${preset}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {value.preset === 'custom' && (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
          <Box sx={{ display: 'flex', gap: 2 }}>
            {renderDatePicker('start')}
            {renderDatePicker('end')}
          </Box>
        </LocalizationProvider>
      )}
    </Box>
  );
};

export default CallAnalysisPeriodFilter;
